import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import ActivityFeed from '@/components/ActivityFeed';
import AcertijoSection from '@/components/AcertijoSection';
import QuoteOfTheDay from '@/components/QuoteOfTheDay';
import KlanPulse from '@/components/KlanPulse';
import ResumenSemanal from '@/components/ResumenSemanal';
import TrainingPulseBadges from '@/components/TrainingPulseBadges';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, Clock, Users, MapPin, Cake, Heart, HeartOff, User, UserX, ImageIcon, X, Settings, ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Trophy, Dumbbell } from 'lucide-react';
import { format, startOfWeek, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const TIPO_FRASES_MOTIVADORAS = {
  carrera: '¡A correr, lobo! 🏃‍♂️',
  ciclismo: '¡A pedalear! 🚴',
  fuerza: '¡A levantar peso! 💪',
  funcional: '¡A sudar! 💦',
  natacion: '¡Al agua! 🏊',
  yoga: 'Respira y sumérgete 🧘',
  default: '¡A por ello! 💪',
};

function fraseApuntado(nombre, training) {
  const firstName = (nombre || '').split(' ')[0] || 'lobo';
  const f = TIPO_FRASES_MOTIVADORAS[training?.tipo] || TIPO_FRASES_MOTIVADORAS.default;
  return `¡${f.replace('¡', '')} ${firstName}, te esperamos.`;
}

function lanzaConfetti() {
  try {
    const colors = ['#d4af37', '#f5d56a', '#ffffff'];
    confetti({
      particleCount: 60,
      spread: 70,
      origin: { y: 0.7 },
      colors,
      ticks: 120,
      gravity: 1,
      scalar: 0.9,
    });
  } catch (e) { /* no-op */ }
}

function Dashboard() {
  const [trainings, setTrainings] = useState([]);
  const [users, setUsers] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [selectedBirthdayUser, setSelectedBirthdayUser] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [showBgDialog, setShowBgDialog] = useState(false);
  const [bgInputUrl, setBgInputUrl] = useState('');
  const [bgFile, setBgFile] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);
  
  const isPro = user?.es_pro || user?.role === 'entrenador' || user?.role === 'client_plus';
  const [uploadingBg, setUploadingBg] = useState(false);

  // Eventos del Klan (competición / evento externo creados por miembros)
  const emptyEvento = { tipo: 'competicion', nombre: '', fecha: '', hora: '10:00', descripcion: '', ubicacion_nombre: '', ubicacion_maps: '' };
  const [showEventoDialog, setShowEventoDialog] = useState(false);
  const [editingEventoId, setEditingEventoId] = useState(null);
  const [eventoForm, setEventoForm] = useState(emptyEvento);
  const [savingEvento, setSavingEvento] = useState(false);

  const openNewEvento = () => {
    setEditingEventoId(null);
    setEventoForm({ ...emptyEvento, fecha: format(new Date(), 'yyyy-MM-dd') });
    setShowEventoDialog(true);
  };

  const openEditEvento = (t) => {
    setEditingEventoId(t.id);
    setEventoForm({
      tipo: t.tipo, nombre: t.nombre_evento || '', fecha: t.fecha, hora: t.hora,
      descripcion: t.descripcion_evento || '', ubicacion_nombre: t.ubicacion_nombre || '', ubicacion_maps: t.ubicacion_maps || ''
    });
    setSelectedTraining(null);
    setShowEventoDialog(true);
  };

  // Creación rápida de entrenos pinchando un día del calendario (solo entrenador)
  const QC_TIPOS = ['carrera', 'funcional', 'fuerza', 'competicion', 'carrera_mediciones', 'especial', 'excursion', 'natacion', 'bike'];
  const QC_DIAS = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
  const [quickCreateFecha, setQuickCreateFecha] = useState(null);
  const [qcForm, setQcForm] = useState({ tipo: 'carrera', hora: '18:30', max_participantes: 14, ubicacion_id: 'none', plazas_abiertas: true });
  const [ubicaciones, setUbicaciones] = useState([]);
  const [entrenosList, setEntrenosList] = useState([]);
  const [savingQc, setSavingQc] = useState(false);

  useEffect(() => {
    if (user?.role === 'entrenador') {
      axios.get(`${API}/entrenos`).then(r => setEntrenosList(r.data || [])).catch(() => {});
    }
  }, [user?.role]);

  // Entrenos (plantillas) agrupados por su fecha, normalizando DD/MM/YYYY → YYYY-MM-DD
  const entrenosPorFecha = useMemo(() => {
    const map = {};
    entrenosList.forEach(en => {
      let f = en.fecha;
      if (!f) return;
      if (f.includes('/')) {
        const [d, m, y] = f.split('/');
        f = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
      (map[f] = map[f] || []).push(en);
    });
    return map;
  }, [entrenosList]);
  const [entrenosDiaView, setEntrenosDiaView] = useState(null);

  const openQuickCreate = async (date) => {
    setQcForm({ tipo: 'carrera', hora: '18:30', max_participantes: 14, ubicacion_id: 'none', plazas_abiertas: true, entreno_id: 'none' });
    setQuickCreateFecha(format(date, 'yyyy-MM-dd'));
    if (ubicaciones.length === 0) {
      try {
        const r = await axios.get(`${API}/trainings/ubicaciones`);
        setUbicaciones(r.data || []);
      } catch (e) { /* no-op */ }
    }
  };

  const saveQuickCreate = async () => {
    if (!qcForm.hora) { toast.error('La hora es obligatoria'); return; }
    setSavingQc(true);
    try {
      const d = new Date(quickCreateFecha + 'T00:00:00');
      await axios.post(`${API}/trainings`, {
        tipo: qcForm.tipo,
        dia_semana: QC_DIAS[d.getDay()],
        fecha: quickCreateFecha,
        hora: qcForm.hora,
        max_participantes: parseInt(qcForm.max_participantes) || 14,
        plazas_abiertas: qcForm.plazas_abiertas,
        ubicacion_id: qcForm.ubicacion_id === 'none' ? null : qcForm.ubicacion_id,
        entreno_id: qcForm.entreno_id && qcForm.entreno_id !== 'none' ? qcForm.entreno_id : null,
      });
      toast.success('Entreno creado 💪');
      setQuickCreateFecha(null);
      fetchTrainings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear el entreno');
    } finally {
      setSavingQc(false);
    }
  };

  const saveEvento = async () => {
    if (!eventoForm.nombre.trim() || !eventoForm.fecha || !eventoForm.hora) {
      toast.error('Nombre, fecha y hora son obligatorios');
      return;
    }
    setSavingEvento(true);
    try {
      if (editingEventoId) await axios.put(`${API}/eventos-klan/${editingEventoId}`, eventoForm);
      else await axios.post(`${API}/eventos-klan`, eventoForm);
      toast.success(editingEventoId ? 'Evento actualizado' : '¡Evento creado! Ya lo ve todo el Klan 🎉');
      setShowEventoDialog(false);
      fetchTrainings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar el evento');
    } finally {
      setSavingEvento(false);
    }
  };

  const deleteEvento = async (id) => {
    if (!window.confirm('¿Borrar este evento del calendario?')) return;
    try {
      await axios.delete(`${API}/eventos-klan/${id}`);
      toast.success('Evento borrado');
      setSelectedTraining(null);
      fetchTrainings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al borrar el evento');
    }
  };

  useEffect(() => {
    fetchData();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.calendar_background) {
        const bgUrl = response.data.calendar_background.startsWith('http') 
          ? response.data.calendar_background 
          : `${BACKEND_URL}${response.data.calendar_background}`;
        setBackgroundImage(bgUrl);
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/trainings`),
        user?.role === 'entrenador' ? axios.get(`${API}/users`) : axios.get(`${API}/users/public`),
        user?.role === 'entrenador' ? axios.get(`${API}/users/birthdays/upcoming`) : Promise.resolve({ data: [] })
      ];
      
      const [trainingsRes, usersRes, birthdaysRes] = await Promise.all(requests);
      setTrainings(trainingsRes.data);
      setUpcomingBirthdays(birthdaysRes.data);
      setAllUsers(usersRes.data);
      
      // Crear mapa de usuarios por ID
      const usersMap = {};
      usersRes.data.forEach(u => {
        usersMap[u.id] = u;
      });
      setUsers(usersMap);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Obtener cumpleaños para un día específico
  const getBirthdaysForDay = (date) => {
    if (!user?.role === 'entrenador') return [];
    const dayOfMonthToCheck = date.getDate();
    const monthToCheck = date.getMonth() + 1; // getMonth() devuelve 0-11
    return allUsers.filter(u => {
      if (!u.fecha_nacimiento) return false;
      const [, month, dayOfMonth] = u.fecha_nacimiento.split('-');
      return parseInt(month) === monthToCheck && parseInt(dayOfMonth) === dayOfMonthToCheck;
    });
  };

  const fetchTrainings = async () => {
    try {
      const response = await axios.get(`${API}/trainings`);
      setTrainings(response.data);
    } catch (error) {
      console.error('Error al cargar entrenamientos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Contar solo miembros (no entrenadores) para el límite de plazas
  const countMiembros = (training) => {
    if (!training || !training.participantes) return 0;
    const miembros = training.participantes.filter(pId => {
      const usuario = users[pId];
      return usuario && usuario.role !== 'entrenador';
    }).length;
    const invitados = (training.invitados || []).length;
    return miembros + invitados;
  };

  const handleJoinTraining = async (trainingId) => {
    try {
      await axios.post(`${API}/trainings/${trainingId}/join`);
      const training = trainings.find(t => t.id === trainingId);
      lanzaConfetti();
      toast.success(fraseApuntado(user?.nombre, training), {
        description: training ? `${training.tipo?.toUpperCase()} · ${training.fecha} · ${training.hora}` : null,
        duration: 4000,
      });
      fetchTrainings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al apuntarse al entreno');
    }
  };

  const handleLeaveTraining = async (trainingId) => {
    try {
      await axios.delete(`${API}/trainings/${trainingId}/leave`);
      toast('Te has desapuntado del entreno', {
        description: 'Otro lobo aprovechará tu plaza 🦁',
      });
      fetchTrainings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desapuntarse del entreno');
    }
  };

  const handleAddInvitado = async (trainingId) => {
    try {
      const res = await axios.post(`${API}/trainings/${trainingId}/invitado`);
      lanzaConfetti();
      toast.success('¡+1 invitado apuntado! 🎉', {
        description: 'Recuérdale puntualidad y respeto al Klan.',
      });
      await fetchTrainings();
      setSelectedTraining(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al apuntar invitado');
    }
  };

  const handleRemoveInvitado = async (trainingId) => {
    try {
      const res = await axios.delete(`${API}/trainings/${trainingId}/invitado`);
      await fetchTrainings();
      setSelectedTraining(res.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al quitar invitado');
    }
  };

  const getTrainingsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return trainings.filter((t) => t.fecha === dateStr);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const getUbicacionStyle = (ubicacion_id) => {
    // Colores distintivos por ubicación física
    switch (ubicacion_id) {
      case 'cabana':       // Campo de fútbol La Cabana → verde césped
        return { dot: 'bg-green-400', text: 'text-green-400', label: 'Cabana' };
      case 'mk_car':       // Centro Alto Rendimiento MK → dorado (marca)
        return { dot: 'bg-gold', text: 'text-gold', label: 'MK' };
      case 'biosaludable': // Parque Biosaludable → azul agua
        return { dot: 'bg-sky-400', text: 'text-sky-400', label: 'Bio' };
      case 'illes':        // Parque Illes → naranja
        return { dot: 'bg-orange-400', text: 'text-orange-400', label: 'Illes' };
      case 'otros':        // Ubicación custom → violeta
        return { dot: 'bg-violet-400', text: 'text-violet-400', label: 'Otros' };
      default:
        return null;
    }
  };

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case 'carrera':
        return { bg: 'bg-blue-600', border: 'border-blue-500', text: 'text-blue-400', badge: 'bg-blue-500' };
      case 'funcional':
        return { bg: 'bg-yellow-600', border: 'border-yellow-500', text: 'text-yellow-400', badge: 'bg-yellow-500' };
      case 'fuerza':
        return { bg: 'bg-amber-600', border: 'border-amber-500', text: 'text-amber-400', badge: 'bg-amber-500' };
      case 'competicion':
        return { bg: 'bg-red-600', border: 'border-red-500', text: 'text-red-400', badge: 'bg-red-500' };
      case 'evento_externo':
        return { bg: 'bg-indigo-600', border: 'border-indigo-500', text: 'text-indigo-400', badge: 'bg-indigo-500' };
      case 'especial':
        return { bg: 'bg-pink-600', border: 'border-pink-500', text: 'text-pink-400', badge: 'bg-pink-500' };
      case 'carrera_mediciones':
        return { bg: 'bg-purple-600', border: 'border-purple-500', text: 'text-purple-400', badge: 'bg-purple-500' };
      case 'excursion':
        return { bg: 'bg-green-600', border: 'border-green-500', text: 'text-green-400', badge: 'bg-green-500' };
      case 'natacion':
        return { bg: 'bg-cyan-600', border: 'border-cyan-500', text: 'text-cyan-400', badge: 'bg-cyan-500' };
      case 'bike':
        return { bg: 'bg-orange-600', border: 'border-orange-500', text: 'text-orange-400', badge: 'bg-orange-500' };
      default:
        return { bg: 'bg-gray-600', border: 'border-gray-500', text: 'text-gray-400', badge: 'bg-gray-500' };
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'carrera': return 'Carrera';
      case 'funcional': return 'Funcional';
      case 'fuerza': return 'Fuerza';
      case 'competicion': return 'Competicion';
      case 'evento_externo': return 'Evento externo';
      case 'especial': return 'Especial';
      case 'carrera_mediciones': return 'Carrera + Mediciones';
      case 'excursion': return 'Excursion';
      case 'natacion': return 'Natacion';
      case 'bike': return 'Bike';
      default: return tipo.charAt(0).toUpperCase() + tipo.slice(1);
    }
  };

  // Funciones para imagen de fondo
  const handleBgFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBgFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setBgPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const saveBgImage = async () => {
    setUploadingBg(true);
    try {
      const formData = new FormData();
      
      if (bgFile) {
        formData.append('background_file', bgFile);
      } else if (bgInputUrl) {
        formData.append('background_url', bgInputUrl);
      } else {
        alert('Por favor, selecciona una imagen o introduce una URL');
        setUploadingBg(false);
        return;
      }
      
      const response = await axios.put(`${API}/settings/calendar-background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const newBgUrl = response.data.calendar_background;
      if (newBgUrl) {
        const fullUrl = newBgUrl.startsWith('http') ? newBgUrl : `${BACKEND_URL}${newBgUrl}`;
        setBackgroundImage(fullUrl);
      }
      
      alert('Imagen de fondo guardada');
      setShowBgDialog(false);
      setBgInputUrl('');
      setBgFile(null);
      setBgPreview(null);
    } catch (error) {
      alert('Error al guardar imagen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingBg(false);
    }
  };

  const removeBgImage = async () => {
    try {
      await axios.delete(`${API}/settings/calendar-background`);
      setBackgroundImage('');
      setShowBgDialog(false);
      alert('Imagen de fondo eliminada');
    } catch (error) {
      alert('Error al eliminar imagen: ' + error.message);
    }
  };

  // Obtener días del mes
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Días del mes anterior para llenar
    const startOffset = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
    for (let i = startOffset; i > 0; i--) {
      const prevDate = new Date(year, month, -i + 1);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    // Días del mes actual
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Días del siguiente mes para completar
    const remainingDays = 42 - days.length; // 6 semanas x 7 días
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="mb-4 sm:mb-8">
        {/* Muro movido al final */}

          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold text-gold" data-testid="dashboard-title">
                Calendario de Entrenamientos
              </h1>
              <p className="text-gold text-sm sm:text-lg italic font-medium mt-1">Aquí empieza el cambio</p>
              <QuoteOfTheDay />
            </div>
            <div className="flex flex-wrap gap-2">
              {user?.role === 'entrenador' && (
                <Button 
                  onClick={() => setShowBgDialog(true)} 
                  variant="outline" 
                  className="text-white border-gray-600 hover:border-gold text-xs sm:text-sm"
                  size="sm"
                  data-testid="btn-config-bg"
                >
                  <ImageIcon className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Fondo</span>
                </Button>
              )}
              <Button onClick={goToPreviousMonth} className="btn-primary-mk text-xs sm:text-sm" size="sm" data-testid="btn-prev-month">
                ←
              </Button>
              <Button onClick={goToToday} variant="outline" className="text-white border-gold text-xs sm:text-sm" size="sm" data-testid="btn-today">
                Hoy
              </Button>
              <Button onClick={goToNextMonth} className="btn-primary-mk text-xs sm:text-sm" size="sm" data-testid="btn-next-month">
                →
              </Button>
            </div>
          </div>

          {/* KlanPulse: saludo + pulso + roster de hoy */}
          <KlanPulse />

          {/* Resumen semanal (lunes) */}
          <ResumenSemanal />

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={goToPreviousMonth}
                  className="text-gold hover:bg-gold/10 rounded-lg p-1.5 transition-colors"
                  aria-label="Mes anterior"
                  data-testid="btn-prev-month-inline"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <h2 className="text-xl sm:text-2xl text-white capitalize">
                  {format(currentMonth, "MMMM yyyy", { locale: es })}
                </h2>
                <button
                  onClick={goToNextMonth}
                  className="text-gold hover:bg-gold/10 rounded-lg p-1.5 transition-colors"
                  aria-label="Mes siguiente"
                  data-testid="btn-next-month-inline"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <button
                  onClick={goToToday}
                  className="text-xs text-gold border border-gold/40 rounded-full px-3 py-1 hover:bg-gold/10 transition-colors ml-1"
                  data-testid="btn-today-inline"
                >
                  Hoy
                </button>
                <button
                  onClick={openNewEvento}
                  className="text-xs text-indigo-300 border border-indigo-400/40 rounded-full px-3 py-1 hover:bg-indigo-500/10 transition-colors flex items-center gap-1"
                  data-testid="btn-new-evento"
                >
                  <Plus className="h-3.5 w-3.5" /> Evento
                </button>
              </div>
              <div className="flex items-center gap-2 mt-1">
                {isPro && (
                  <Badge className="bg-gradient-to-r from-yellow-600 to-yellow-400 text-black font-bold border-0 text-[10px] px-1.5 h-5">
                    PRO
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Alerta compacta de cumpleaños próximos */}
            {user?.role === 'entrenador' && upcomingBirthdays.length > 0 && (
              <div className="flex items-center gap-2 bg-pink-900/30 border border-pink-500/30 rounded-full px-3 py-1.5 self-start sm:self-auto">
                <Cake className="h-4 w-4 text-pink-400" />
                <div className="flex items-center gap-1">
                  {upcomingBirthdays.slice(0, 3).map((birthday, idx) => (
                    <button
                      key={birthday.id}
                      onClick={() => setSelectedBirthdayUser(birthday)}
                      className="flex items-center gap-1 hover:bg-pink-800/30 rounded-full px-2 py-0.5 transition-colors"
                      title={`${birthday.nombre} - ${birthday.dias_restantes === 0 ? '¡Hoy!' : `En ${birthday.dias_restantes} días`}`}
                    >
                      {birthday.foto_perfil ? (
                        <img 
                          src={`${BACKEND_URL}${birthday.foto_perfil}`} 
                          alt={birthday.nombre}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gold flex items-center justify-center">
                          <User className="w-3 h-3 text-gray-900" />
                        </div>
                      )}
                      <span className="text-pink-300 text-xs">
                        {birthday.dias_restantes === 0 ? '🎂' : birthday.dias_restantes + 'd'}
                      </span>
                    </button>
                  ))}
                  {upcomingBirthdays.length > 3 && (
                    <span className="text-pink-400 text-xs">+{upcomingBirthdays.length - 3}</span>
                  )}
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/notifications/check-birthdays`);
                      alert(`✅ ${res.data.message}\n🎂 Cumpleaños próximos: ${res.data.cumpleanos_proximos}\n🔔 Notificaciones creadas: ${res.data.notificaciones_creadas}`);
                    } catch (err) {
                      alert('Error al verificar cumpleaños');
                    }
                  }}
                  className="ml-2 text-xs text-pink-400 hover:text-pink-300 underline"
                  title="Crear notificaciones de cumpleaños"
                >
                  🔔
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Modal de detalles del cumpleaños */}
        <Dialog open={!!selectedBirthdayUser} onOpenChange={() => setSelectedBirthdayUser(null)}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
            {selectedBirthdayUser && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-gold flex items-center gap-2">
                    <Cake className="h-5 w-5 text-pink-400" />
                    Cumpleaños de {selectedBirthdayUser.nombre}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    {selectedBirthdayUser.foto_perfil ? (
                      <img 
                        src={`${BACKEND_URL}${selectedBirthdayUser.foto_perfil}`} 
                        alt={selectedBirthdayUser.nombre}
                        className="w-20 h-20 rounded-full object-cover border-2 border-gold"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gold flex items-center justify-center">
                        <User className="w-10 h-10 text-gray-900" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-xl font-bold text-white">{selectedBirthdayUser.nombre}</h3>
                      <p className="text-gray-400 text-sm">{selectedBirthdayUser.email}</p>
                      <p className="text-pink-400 font-medium">
                        Cumple {selectedBirthdayUser.edad_cumplira} años
                        {selectedBirthdayUser.dias_restantes === 0 
                          ? ' ¡HOY!' 
                          : selectedBirthdayUser.dias_restantes === 1 
                            ? ' MAÑANA' 
                            : ` el ${format(new Date(selectedBirthdayUser.fecha_cumple), "d 'de' MMMM", { locale: es })}`}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-gray-700">
                    <h4 className="text-gray-400 font-medium">Preferencias de entreno</h4>
                    
                    {selectedBirthdayUser.entreno_favorito ? (
                      <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
                          <Heart className="h-4 w-4" />
                          Le gusta
                        </div>
                        <p className="text-gray-300 text-sm">{selectedBirthdayUser.entreno_favorito}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No ha especificado qué le gusta</p>
                    )}

                    {selectedBirthdayUser.entreno_menos_favorito ? (
                      <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-red-400 text-sm mb-1">
                          <HeartOff className="h-4 w-4" />
                          No le gusta
                        </div>
                        <p className="text-gray-300 text-sm">{selectedBirthdayUser.entreno_menos_favorito}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No ha especificado qué no le gusta</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog entrenos planificados del día (solo coach, por fecha del entreno) */}
        <Dialog open={!!entrenosDiaView} onOpenChange={(o) => { if (!o) setEntrenosDiaView(null); }}>
          <DialogContent className="bg-gray-900 border-gold/30 text-white max-w-sm max-h-[80vh] overflow-y-auto" data-testid="entrenos-dia-dialog">
            <DialogHeader>
              <DialogTitle className="text-gold">
                Entreno del {entrenosDiaView && format(new Date(entrenosDiaView.fecha + 'T00:00:00'), "EEEE d 'de' MMMM", { locale: es })}
              </DialogTitle>
            </DialogHeader>
            <p className="text-gray-500 text-[11px] -mt-2">Solo tú puedes ver esto</p>
            <div className="space-y-3">
              {entrenosDiaView?.items.map(en => (
                <div key={en.id} className="p-3 bg-gray-800 rounded-lg border border-gold/30">
                  <p className="text-white font-bold">{en.codigo} — {en.dios} <span className="text-gray-400 font-normal text-xs">(intensidad {en.intensidad}/10)</span></p>
                  {en.puesta_en_marcha && (
                    <div className="mt-2">
                      <p className="text-amber-400 text-[10px] font-bold uppercase">Puesta en marcha</p>
                      <p className="text-gray-300 text-xs whitespace-pre-line">{en.puesta_en_marcha}</p>
                    </div>
                  )}
                  {en.entreno_principal && (
                    <div className="mt-2">
                      <p className="text-amber-400 text-[10px] font-bold uppercase">Entreno principal</p>
                      <p className="text-gray-300 text-xs whitespace-pre-line">{en.entreno_principal}</p>
                    </div>
                  )}
                  {en.vuelta_a_la_calma && (
                    <div className="mt-2">
                      <p className="text-amber-400 text-[10px] font-bold uppercase">Vuelta a la calma</p>
                      <p className="text-gray-300 text-xs whitespace-pre-line">{en.vuelta_a_la_calma}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog creación rápida de entreno (coach, desde el calendario) */}
        <Dialog open={!!quickCreateFecha} onOpenChange={(o) => { if (!o) setQuickCreateFecha(null); }}>
          <DialogContent className="bg-gray-900 border-gold/30 text-white max-w-sm" data-testid="quick-create-dialog">
            <DialogHeader>
              <DialogTitle className="text-gold">
                Nuevo entreno · {quickCreateFecha && format(new Date(quickCreateFecha + 'T00:00:00'), "EEEE d 'de' MMMM", { locale: es })}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-gray-300 text-xs">Tipo</Label>
                <Select value={qcForm.tipo} onValueChange={(v) => setQcForm({ ...qcForm, tipo: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="qc-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    {QC_TIPOS.map(t => (
                      <SelectItem key={t} value={t}>{t === 'carrera_mediciones' ? 'Carrera + Mediciones' : t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300 text-xs">Hora</Label>
                  <Input type="time" value={qcForm.hora} onChange={(e) => setQcForm({ ...qcForm, hora: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white" data-testid="qc-hora-input" />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs">Plazas máx</Label>
                  <Input type="number" min="1" value={qcForm.max_participantes}
                    onChange={(e) => setQcForm({ ...qcForm, max_participantes: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white" data-testid="qc-plazas-input" />
                </div>
              </div>
              <div>
                <Label className="text-gray-300 text-xs">Ubicación</Label>
                <Select value={qcForm.ubicacion_id} onValueChange={(v) => setQcForm({ ...qcForm, ubicacion_id: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="qc-ubicacion-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="none">Sin ubicación</SelectItem>
                    {ubicaciones.filter(u => u.id !== 'otros').map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300 text-xs">Entreno del día (solo lo ves tú)</Label>
                <Select value={qcForm.entreno_id} onValueChange={(v) => setQcForm({ ...qcForm, entreno_id: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="qc-entreno-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
                    <SelectItem value="none">Sin entreno asignado</SelectItem>
                    {entrenosList.map(en => (
                      <SelectItem key={en.id} value={en.id}>{en.codigo} — {en.dios}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={qcForm.plazas_abiertas}
                  onChange={(e) => setQcForm({ ...qcForm, plazas_abiertas: e.target.checked })}
                  className="accent-gold w-4 h-4" data-testid="qc-plazas-abiertas-check" />
                <span className="text-gray-300 text-sm">Plazas abiertas (se pueden apuntar ya)</span>
              </label>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1 border-gray-700 text-gray-300" onClick={() => setQuickCreateFecha(null)} data-testid="qc-cancel-btn">
                  Cancelar
                </Button>
                <Button className="flex-1 bg-gold text-gray-900 hover:bg-gold/80 font-bold" onClick={saveQuickCreate} disabled={savingQc} data-testid="qc-save-btn">
                  {savingQc ? 'Creando...' : 'Crear entreno'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog crear/editar evento del Klan */}
        <Dialog open={showEventoDialog} onOpenChange={setShowEventoDialog}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                {editingEventoId ? 'Editar evento' : 'Nuevo evento del Klan'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setEventoForm(f => ({ ...f, tipo: 'competicion' }))}
                  className={`rounded-lg px-3 py-2.5 text-sm font-semibold border transition-colors ${eventoForm.tipo === 'competicion' ? 'bg-red-500/20 border-red-500 text-red-300' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'}`}
                  data-testid="evento-tipo-competicion"
                >
                  🏆 Competición
                </button>
                <button
                  onClick={() => setEventoForm(f => ({ ...f, tipo: 'evento_externo' }))}
                  className={`rounded-lg px-3 py-2.5 text-sm font-semibold border transition-colors ${eventoForm.tipo === 'evento_externo' ? 'bg-indigo-500/20 border-indigo-500 text-indigo-300' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'}`}
                  data-testid="evento-tipo-externo"
                >
                  📌 Evento externo
                </button>
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Nombre *</Label>
                <Input
                  value={eventoForm.nombre}
                  onChange={(e) => setEventoForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Ej: Media Maratón de Palma"
                  className="bg-gray-800 border-gray-700"
                  data-testid="evento-nombre"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-gray-400 text-xs">Fecha *</Label>
                  <Input
                    type="date"
                    value={eventoForm.fecha}
                    onChange={(e) => setEventoForm(f => ({ ...f, fecha: e.target.value }))}
                    className="bg-gray-800 border-gray-700"
                    data-testid="evento-fecha"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-400 text-xs">Hora *</Label>
                  <Input
                    type="time"
                    value={eventoForm.hora}
                    onChange={(e) => setEventoForm(f => ({ ...f, hora: e.target.value }))}
                    className="bg-gray-800 border-gray-700"
                    data-testid="evento-hora"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Descripción (opcional)</Label>
                <textarea
                  value={eventoForm.descripcion}
                  onChange={(e) => setEventoForm(f => ({ ...f, descripcion: e.target.value }))}
                  placeholder="Detalles, distancia, punto de encuentro..."
                  rows={2}
                  className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm outline-none focus:border-gold resize-none"
                  data-testid="evento-descripcion"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Ubicación (opcional)</Label>
                <Input
                  value={eventoForm.ubicacion_nombre}
                  onChange={(e) => setEventoForm(f => ({ ...f, ubicacion_nombre: e.target.value }))}
                  placeholder="Ej: Playa de Palma"
                  className="bg-gray-800 border-gray-700"
                  data-testid="evento-ubicacion"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Enlace Google Maps (opcional)</Label>
                <Input
                  value={eventoForm.ubicacion_maps}
                  onChange={(e) => setEventoForm(f => ({ ...f, ubicacion_maps: e.target.value }))}
                  placeholder="https://maps.app.goo.gl/..."
                  className="bg-gray-800 border-gray-700"
                  data-testid="evento-maps"
                />
              </div>
              <p className="text-[11px] text-gray-500">Visible para todo el Klan. Sin XP ni límite de plazas: la gente se apunta solo para que sepáis quién va.</p>
              <Button onClick={saveEvento} disabled={savingEvento} className="w-full btn-primary-mk" data-testid="btn-save-evento">
                {savingEvento ? 'Guardando...' : editingEventoId ? 'Guardar cambios' : 'Crear evento'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog para configurar fondo del calendario */}
        <Dialog open={showBgDialog} onOpenChange={setShowBgDialog}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Configurar Fondo del Calendario
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bg-url">URL de imagen</Label>
                <Input
                  id="bg-url"
                  value={bgInputUrl}
                  onChange={(e) => setBgInputUrl(e.target.value)}
                  placeholder="https://ejemplo.com/imagen.jpg"
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="input-bg-url"
                />
              </div>
              
              <div className="text-center text-gray-500">o</div>
              
              <div className="space-y-2">
                <Label htmlFor="bg-file">Subir imagen</Label>
                <Input
                  id="bg-file"
                  type="file"
                  accept="image/*"
                  onChange={handleBgFileChange}
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="input-bg-file"
                />
              </div>
              
              {bgPreview && (
                <div className="relative">
                  <img src={bgPreview} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                  <button
                    onClick={() => { setBgFile(null); setBgPreview(null); }}
                    className="absolute top-2 right-2 p-1 bg-red-500 rounded-full"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                </div>
              )}
              
              {backgroundImage && !bgPreview && (
                <div className="space-y-2">
                  <Label>Imagen actual</Label>
                  <img src={backgroundImage} alt="Fondo actual" className="w-full h-32 object-cover rounded-lg" />
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  onClick={saveBgImage} 
                  disabled={uploadingBg}
                  className="flex-1 btn-primary-mk"
                  data-testid="btn-save-bg"
                >
                  {uploadingBg ? 'Guardando...' : 'Guardar'}
                </Button>
                {backgroundImage && (
                  <Button 
                    onClick={removeBgImage} 
                    variant="destructive"
                    data-testid="btn-remove-bg"
                  >
                    Quitar
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <div 
            className="rounded-lg p-2 sm:p-4 relative"
            style={{
              backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundColor: backgroundImage ? 'transparent' : 'rgb(17 24 39)'
            }}
            data-testid="calendar-container"
          >
            {/* Overlay oscuro para mejorar legibilidad */}
            {backgroundImage && (
              <div className="absolute inset-0 bg-black/60 rounded-lg"></div>
            )}
            
            <div className="relative z-10">
              {/* Encabezados de días */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((day, i) => (
                  <div key={day} className="text-center text-gold font-semibold py-1 sm:py-2 text-xs sm:text-base">
                    <span className="sm:hidden">{day}</span>
                    <span className="hidden sm:inline">{['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'][i]}</span>
                  </div>
                ))}
              </div>

            {/* Días del mes */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {getDaysInMonth().map((dayInfo, index) => {
                const dayTrainings = getTrainingsForDate(dayInfo.date);
                const isTodayDate = isToday(dayInfo.date);

                return (
                  <div
                    key={index}
                    className={`min-h-[60px] sm:min-h-[120px] border rounded-lg p-1 sm:p-2 ${
                      dayInfo.isCurrentMonth 
                        ? isTodayDate 
                          ? 'bg-gray-800 border-gold border-2' 
                          : 'bg-gray-800 border-gray-700'
                        : 'bg-gray-950 border-gray-800 opacity-50'
                    }`}
                    data-testid={`calendar-day-${format(dayInfo.date, 'yyyy-MM-dd')}`}
                  >
                    <div className="flex items-start justify-between mb-1 sm:mb-2">
                      <div className={`text-xs sm:text-sm font-semibold ${
                        dayInfo.isCurrentMonth 
                          ? isTodayDate 
                            ? 'text-gold' 
                            : 'text-white'
                          : 'text-gray-600'
                      }`}>
                        {format(dayInfo.date, 'd')}
                      </div>
                      {user?.role === 'entrenador' && dayInfo.isCurrentMonth && (
                        <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                          {entrenosPorFecha[format(dayInfo.date, 'yyyy-MM-dd')] && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setEntrenosDiaView({ fecha: format(dayInfo.date, 'yyyy-MM-dd'), items: entrenosPorFecha[format(dayInfo.date, 'yyyy-MM-dd')] }); }}
                              className="w-4 h-4 sm:w-5 sm:h-5 rounded bg-amber-500/30 hover:bg-amber-500/60 text-amber-300 flex items-center justify-center transition-colors"
                              title="Entreno planificado este día (solo tú lo ves)"
                              data-testid={`entreno-dia-chip-${format(dayInfo.date, 'yyyy-MM-dd')}`}
                            >
                              <Dumbbell className="w-3 h-3" />
                            </button>
                          )}
                          <button
                            onClick={() => openQuickCreate(dayInfo.date)}
                            className="w-4 h-4 sm:w-5 sm:h-5 rounded bg-gold/15 hover:bg-gold/40 text-gold flex items-center justify-center transition-colors"
                            title="Añadir entreno este día"
                            data-testid={`quick-add-training-${format(dayInfo.date, 'yyyy-MM-dd')}`}
                          >
                            <Plus className="w-3 h-3" strokeWidth={3} />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1">
                      {dayTrainings.map((training) => {
                        const colors = getTipoColor(training.tipo);
                        const ubiStyle = getUbicacionStyle(training.ubicacion_id);
                        const isParticipating = training.participantes.includes(user?.id);
                        const proHighlight = isPro && isParticipating ? 'ring-1 ring-gold shadow-[0_0_8px_rgba(245,158,11,0.3)]' : '';
                        const customColor = (training.tipo === 'especial' && training.color) ? training.color : null;
                        const cardStyle = customColor ? { backgroundColor: `${customColor}22`, borderLeftColor: customColor } : undefined;
                        const badgeStyle = customColor ? { backgroundColor: customColor } : undefined;

                        return (
                          <div
                            key={training.id}
                            className={`relative rounded p-1 sm:rounded-lg sm:p-2 cursor-pointer transition-all hover:scale-[1.02] ${customColor ? 'border-l-2 sm:border-l-4' : `${colors.bg} bg-opacity-20 border-l-2 sm:border-l-4 ${colors.border}`} ${proHighlight} overflow-hidden`}
                            style={cardStyle}
                            onClick={() => setSelectedTraining(training)}
                            data-testid={`training-card-${training.id}`}
                          >
                            {/* Ribete superior con ubicación */}
                            {ubiStyle && (
                              <div
                                className={`-mx-1 sm:-mx-2 -mt-1 sm:-mt-2 mb-1 px-1 py-0.5 ${ubiStyle.dot} flex items-center justify-center gap-0.5 sm:gap-1 shadow-sm`}
                                title={training.ubicacion_nombre || ubiStyle.label}
                                data-testid={`training-ubi-pin-${training.id}`}
                              >
                                <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-900" strokeWidth={3} />
                                <span className="text-[8px] sm:text-[10px] font-extrabold leading-none text-gray-900 uppercase tracking-wide truncate max-w-[90%]">
                                  {training.ubicacion_id === 'otros' && training.ubicacion_nombre ? training.ubicacion_nombre : ubiStyle.label}
                                </span>
                              </div>
                            )}
                            <div className="space-y-0.5 sm:space-y-1">
                              {/* Versión móvil: tipo y hora en dos líneas */}
                              <div className="sm:hidden text-center">
                                <span className={`text-[9px] font-bold px-1 rounded ${customColor ? '' : colors.badge} text-white block`} style={badgeStyle}>
                                  {training.tipo.substring(0, 3).toUpperCase()}
                                </span>
                                <span className="text-[8px] text-white block mt-0.5">
                                  {training.hora}
                                </span>
                              </div>
                              {/* Versión desktop: completa */}
                              <div className="hidden sm:flex items-center justify-between">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${customColor ? '' : colors.badge} text-white`} style={badgeStyle}>
                                  {getTipoLabel(training.tipo)}
                                </span>
                                <span className="text-xs text-white font-medium" data-testid="training-hora">
                                  {training.hora}
                                </span>
                              </div>

                              <div className="flex items-center text-xs text-gray-300">
                                <Users className="w-3 h-3 mr-1" />
                                <span data-testid="training-participantes">
                                  {training.es_evento_klan ? `${countMiembros(training)}` : `${countMiembros(training)}/${training.max_participantes}`}
                                </span>
                              </div>

                              {(training.nombre_evento || (training.tipo === 'competicion' && training.texto_competicion)) && (
                                <div className={`border rounded px-1.5 py-1 mt-1 ${training.tipo === 'evento_externo' ? 'bg-indigo-500/20 border-indigo-500/40' : 'bg-red-500/20 border-red-500/40'}`} data-testid="texto-competicion-cal">
                                  <span className={`text-[10px] sm:text-xs font-bold leading-tight block truncate sm:whitespace-normal sm:break-words ${training.tipo === 'evento_externo' ? 'text-indigo-200' : 'text-red-200'}`}>
                                    {training.nombre_evento || training.texto_competicion}
                                  </span>
                                </div>
                              )}

                              {/* Botones solo visibles en desktop */}
                              <div className="hidden sm:block">
                                {training.plazas_abiertas ? (
                                training.participantes.includes(user?.id) ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className={`w-full text-xs text-white h-6 ${isPro ? 'bg-red-900/40 border-red-500 hover:bg-red-800' : 'bg-red-600 border-red-600 hover:bg-red-700'}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleLeaveTraining(training.id);
                                    }}
                                    data-testid="btn-leave-training"
                                  >
                                    Salir
                                  </Button>
                                ) : countMiembros(training) < training.max_participantes ? (
                                  <Button
                                    size="sm"
                                    className={`w-full text-xs h-6 ${isPro ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400' : 'btn-primary-mk'}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleJoinTraining(training.id);
                                    }}
                                    data-testid="btn-join-training"
                                  >
                                    Apuntar
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full text-xs h-6"
                                    disabled
                                    data-testid="btn-full-training"
                                  >
                                    Lleno
                                  </Button>
                                )
                              ) : (
                                <div className="text-xs text-gray-400 text-center" data-testid="plazas-cerradas">
                                  Cerrado
                                </div>
                              )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Cumpleaños en este día - Solo para entrenador */}
                    {user?.role === 'entrenador' && getBirthdaysForDay(dayInfo.date).map((birthdayUser) => (
                      <button
                        key={`bday-${birthdayUser.id}`}
                        onClick={() => setSelectedBirthdayUser({
                          ...birthdayUser,
                          dias_restantes: 0,
                          edad_cumplira: new Date().getFullYear() - parseInt(birthdayUser.fecha_nacimiento?.split('-')[0] || 0)
                        })}
                        className="w-full mt-1 flex items-center gap-1 bg-pink-900/30 hover:bg-pink-800/40 border border-pink-500/30 rounded px-2 py-1 transition-colors"
                      >
                        <Cake className="w-3 h-3 text-pink-400 flex-shrink-0" />
                        <span className="text-pink-300 text-xs truncate">{birthdayUser.nombre}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
            {/* Leyenda de ubicaciones */}
            <div className="mt-3 pt-3 border-t border-gray-700/50 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[10px] sm:text-xs text-gray-300" data-testid="ubicaciones-legend">
              <span className="text-gray-500 uppercase tracking-wide">Ubicaciones:</span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-green-400 ring-1 ring-gray-900/40"></span>
                Cabana
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-gold ring-1 ring-gray-900/40"></span>
                MK
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-sky-400 ring-1 ring-gray-900/40"></span>
                Biosaludable
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-orange-400 ring-1 ring-gray-900/40"></span>
                Illes
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-violet-400 ring-1 ring-gray-900/40"></span>
                Otros
              </span>
            </div>
            </div>
          </div>
        )}
        
        {/* Dialog para mostrar participantes y apuntarse */}
        <Dialog open={selectedTraining !== null} onOpenChange={() => setSelectedTraining(null)}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {selectedTraining?.tipo && (
                  <Badge className={getTipoColor(selectedTraining.tipo).badge}>
                    {getTipoLabel(selectedTraining.tipo)}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {selectedTraining && (
              <div className="space-y-4">
                {/* Info de evento del Klan */}
                {selectedTraining.es_evento_klan && (
                  <div className="p-3 bg-gray-800 rounded-lg" data-testid="evento-klan-info">
                    <p className="text-white font-bold text-base">{selectedTraining.nombre_evento}</p>
                    {selectedTraining.descripcion_evento && (
                      <p className="text-gray-300 text-sm mt-1 whitespace-pre-wrap">{selectedTraining.descripcion_evento}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      Creado por <span className="text-gold font-semibold">{users[selectedTraining.creado_por]?.nombre || selectedTraining.creado_por_nombre || '—'}</span>
                      <span className="text-gray-500"> · Sin XP · Apúntate para que sepan que vas</span>
                    </p>
                  </div>
                )}
                {selectedTraining.es_evento_klan && (user?.role === 'entrenador' || selectedTraining.creado_por === user?.id) && (
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1 border-gold/40 text-gold hover:bg-gold/10 h-8 text-xs"
                      onClick={() => openEditEvento(selectedTraining)} data-testid="btn-edit-evento">
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                    </Button>
                    <Button variant="outline" className="flex-1 border-red-500/40 text-red-400 hover:bg-red-900/20 h-8 text-xs"
                      onClick={() => deleteEvento(selectedTraining.id)} data-testid="btn-delete-evento">
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Borrar
                    </Button>
                  </div>
                )}
                {/* Info del entreno */}
                <div className="flex items-center gap-4 p-3 bg-gray-800 rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{selectedTraining.fecha?.split('-')[2]}</p>
                    <p className="text-xs text-gray-400 capitalize">
                      {format(new Date(selectedTraining.fecha + 'T00:00:00'), 'MMM', { locale: es })}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold capitalize">{selectedTraining.dia_semana}</p>
                    <p className="text-gold text-lg flex items-center gap-1">
                      <Clock className="h-4 w-4" /> {selectedTraining.hora}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400">{selectedTraining.es_evento_klan ? 'Apuntados' : 'Plazas'}</p>
                    <p className={`text-lg font-bold ${!selectedTraining.es_evento_klan && countMiembros(selectedTraining) >= selectedTraining.max_participantes ? 'text-red-400' : 'text-green-400'}`}>
                      {selectedTraining.es_evento_klan ? countMiembros(selectedTraining) : `${countMiembros(selectedTraining)}/${selectedTraining.max_participantes}`}
                    </p>
                  </div>
                </div>

                {/* Entreno asignado (visible solo para el coach) */}
                {user?.role === 'entrenador' && selectedTraining.entreno_id && (() => {
                  const en = entrenosList.find(e => e.id === selectedTraining.entreno_id);
                  if (!en) return null;
                  return (
                    <div className="p-3 bg-gray-800 rounded-lg border border-gold/30" data-testid="entreno-asignado-info">
                      <p className="text-gold text-[10px] font-bold uppercase tracking-wider mb-1">Entreno del día · solo tú lo ves</p>
                      <p className="text-white font-bold">{en.codigo} — {en.dios} <span className="text-gray-400 font-normal text-xs">(intensidad {en.intensidad}/10)</span></p>
                      {en.puesta_en_marcha && (
                        <div className="mt-2">
                          <p className="text-amber-400 text-[10px] font-bold uppercase">Puesta en marcha</p>
                          <p className="text-gray-300 text-xs whitespace-pre-line">{en.puesta_en_marcha}</p>
                        </div>
                      )}
                      {en.entreno_principal && (
                        <div className="mt-2">
                          <p className="text-amber-400 text-[10px] font-bold uppercase">Entreno principal</p>
                          <p className="text-gray-300 text-xs whitespace-pre-line">{en.entreno_principal}</p>
                        </div>
                      )}
                      {en.vuelta_a_la_calma && (
                        <div className="mt-2">
                          <p className="text-amber-400 text-[10px] font-bold uppercase">Vuelta a la calma</p>
                          <p className="text-gray-300 text-xs whitespace-pre-line">{en.vuelta_a_la_calma}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Estados dinámicos (heat, tiempo restante, amigos apuntados) */}
                <TrainingPulseBadges
                  training={selectedTraining}
                  users={users}
                  countMiembros={countMiembros}
                  currentUserId={user?.id}
                />

                {/* Ubicación */}
                {selectedTraining.ubicacion_nombre && (
                  <a
                    href={selectedTraining.ubicacion_maps || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors group"
                    data-testid="training-location-link"
                  >
                    <div className="bg-gold/20 p-2 rounded-lg">
                      <MapPin className="h-5 w-5 text-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400">Ubicación</p>
                      <p className="text-white font-semibold truncate">{selectedTraining.ubicacion_nombre}</p>
                    </div>
                    {selectedTraining.ubicacion_maps && (
                      <span className="text-xs text-gold group-hover:underline whitespace-nowrap">Ver en mapa</span>
                    )}
                  </a>
                )}

                {/* Acertijo del entreno */}
                {!selectedTraining.es_evento_klan && (user?.role === 'entrenador' || selectedTraining.participantes?.includes(user?.id)) && (
                  <AcertijoSection
                    training={selectedTraining}
                    isEntrenador={user?.role === 'entrenador'}
                    onChanged={fetchTrainings}
                  />
                )}

                {/* Botón de acción */}
                {user?.role === 'entrenador' && !selectedTraining.es_evento_klan && (
                  <Button
                    className={`w-full text-xs h-8 mb-2 ${selectedTraining.plazas_abiertas 
                      ? 'bg-red-600/20 text-red-400 border border-red-500/30 hover:bg-red-600/30' 
                      : 'bg-green-600/20 text-green-400 border border-green-500/30 hover:bg-green-600/30'}`}
                    onClick={async () => {
                      try {
                        await axios.patch(`${API}/trainings/${selectedTraining.id}/toggle-plazas`);
                        fetchData();
                        setSelectedTraining(prev => prev ? {...prev, plazas_abiertas: !prev.plazas_abiertas} : null);
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    data-testid="btn-toggle-plazas"
                  >
                    {selectedTraining.plazas_abiertas ? 'Cerrar plazas' : 'Abrir plazas'}
                  </Button>
                )}
                {selectedTraining.plazas_abiertas ? (
                  <div className="space-y-2">
                    {selectedTraining.participantes.includes(user?.id) ? (
                      <>
                        <Button
                          className="w-full bg-red-600 hover:bg-red-700 text-white"
                          onClick={() => {
                            handleLeaveTraining(selectedTraining.id);
                            setSelectedTraining(null);
                          }}
                        >
                          Salir del entreno
                        </Button>
                        {/* Botón invitado: solo si el usuario está apuntado */}
                        {user?.role !== 'entrenador' && !selectedTraining.es_evento_klan && (
                          (selectedTraining.invitados || []).includes(user?.id) ? (
                            <Button
                              variant="outline"
                              className="w-full border-orange-500 text-orange-400 hover:bg-orange-600/20"
                              onClick={() => handleRemoveInvitado(selectedTraining.id)}
                              data-testid="btn-remove-invitado"
                            >
                              Quitar mi invitado
                            </Button>
                          ) : countMiembros(selectedTraining) < selectedTraining.max_participantes ? (
                            <Button
                              variant="outline"
                              className="w-full border-green-500 text-green-400 hover:bg-green-600/20"
                              onClick={() => handleAddInvitado(selectedTraining.id)}
                              data-testid="btn-add-invitado"
                            >
                              + Apuntar 1 invitado
                            </Button>
                          ) : (
                            <p className="text-center text-xs text-gray-500 py-1">Sin plazas libres para invitados</p>
                          )
                        )}
                      </>
                    ) : countMiembros(selectedTraining) < selectedTraining.max_participantes ? (
                      <Button
                        className="w-full btn-primary-mk"
                        onClick={() => {
                          handleJoinTraining(selectedTraining.id);
                          setSelectedTraining(null);
                        }}
                      >
                        Apuntarme
                      </Button>
                    ) : selectedTraining.lista_espera?.includes(user?.id) ? (
                      <Button
                        variant="outline"
                        className="w-full border-yellow-600 text-yellow-500"
                        onClick={async () => {
                          try {
                            await axios.delete(`${API}/trainings/${selectedTraining.id}/leave-waitlist`);
                            fetchData();
                            setSelectedTraining(null);
                          } catch (error) {
                            alert('Error: ' + error.message);
                          }
                        }}
                      >
                        Salir de lista de espera (posición {(selectedTraining.lista_espera?.indexOf(user?.id) || 0) + 1})
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full border-yellow-600 text-yellow-500 hover:bg-yellow-600/20"
                        onClick={() => {
                          handleJoinTraining(selectedTraining.id);
                          setSelectedTraining(null);
                        }}
                      >
                        Unirse a lista de espera
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center p-3 bg-gray-800 rounded-lg">
                    <p className="text-gray-400">Plazas cerradas</p>
                  </div>
                )}

                {/* Lista de participantes */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" /> Participantes ({selectedTraining.participantes.length}{(selectedTraining.invitados || []).length > 0 ? ` + ${selectedTraining.invitados.length} invitado${selectedTraining.invitados.length > 1 ? 's' : ''}` : ''})
                  </h4>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {selectedTraining.participantes.length > 0 ? (
                      selectedTraining.participantes.map((userId, index) => {
                        const llevaInvitado = (selectedTraining.invitados || []).includes(userId);
                        const esNoShow = (selectedTraining.no_shows || []).includes(userId);
                        return (
                        <div key={userId} className={`flex items-center justify-between gap-2 p-2 rounded-lg ${esNoShow ? 'bg-red-950/40 border border-red-800/50' : 'bg-gray-800'}`}>
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {users[userId]?.foto_perfil ? (
                              <img
                                src={`${BACKEND_URL}${users[userId].foto_perfil}`}
                                alt={users[userId]?.nombre}
                                className={`w-8 h-8 rounded-full object-cover border flex-shrink-0 ${esNoShow ? 'border-red-500 grayscale' : 'border-gold'}`}
                              />
                            ) : (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-gray-900 text-sm flex-shrink-0 ${esNoShow ? 'bg-red-400' : 'bg-gold'}`}>
                                {users[userId]?.nombre?.charAt(0).toUpperCase() || index + 1}
                              </div>
                            )}
                            <span className={`text-sm truncate ${esNoShow ? 'text-red-300 line-through' : 'text-white'}`}>{users[userId]?.nombre || 'Usuario'}</span>
                            {esNoShow && (
                              <span className="text-[10px] font-bold bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-full border border-red-500/40 whitespace-nowrap" data-testid={`badge-noshow-${userId}`}>
                                No vino −50 XP
                              </span>
                            )}
                            {llevaInvitado && (
                              <span className="text-[10px] font-bold bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded-full border border-orange-500/40 whitespace-nowrap" data-testid={`badge-invitado-${userId}`}>
                                +1 invitado
                              </span>
                            )}
                          </div>
                          {user?.role === 'entrenador' && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {!selectedTraining.es_evento_klan && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className={`h-7 w-7 p-0 ${esNoShow ? 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30' : 'text-orange-400 hover:text-orange-300 hover:bg-orange-900/30'}`}
                                title={esNoShow ? 'Devolver los 50 XP (sí vino)' : 'No vino (-50 XP)'}
                                onClick={async () => {
                                  const msg = esNoShow
                                    ? `¿Devolver los 50 XP a ${users[userId]?.nombre || 'este usuario'}?`
                                    : `¿Marcar que ${users[userId]?.nombre || 'este usuario'} NO vino? Se le restarán 50 XP.`;
                                  if (window.confirm(msg)) {
                                    try {
                                      const res = await axios.post(`${API}/trainings/${selectedTraining.id}/no-show/${userId}`);
                                      setSelectedTraining(prev => ({
                                        ...prev,
                                        no_shows: res.data.no_show
                                          ? [...(prev.no_shows || []), userId]
                                          : (prev.no_shows || []).filter(p => p !== userId)
                                      }));
                                      fetchTrainings();
                                    } catch (error) {
                                      alert('Error: ' + (error.response?.data?.detail || error.message));
                                    }
                                  }
                                }}
                                data-testid={`noshow-user-${userId}`}
                              >
                                <UserX className="h-4 w-4" />
                              </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                onClick={async () => {
                                if (window.confirm(`¿Eliminar a ${users[userId]?.nombre || 'este usuario'} del entreno?`)) {
                                  try {
                                    await axios.delete(`${API}/trainings/${selectedTraining.id}/kick/${userId}`);
                                    fetchTrainings();
                                    setSelectedTraining(prev => ({
                                      ...prev,
                                      participantes: prev.participantes.filter(p => p !== userId)
                                    }));
                                  } catch (error) {
                                    alert('Error al eliminar usuario: ' + (error.response?.data?.detail || error.message));
                                  }
                                }
                              }}
                              data-testid={`kick-user-${userId}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                        );
                      })
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-2">No hay participantes aún</p>
                    )}
                  </div>
                </div>

                {/* Lista de espera */}
                {selectedTraining.lista_espera && selectedTraining.lista_espera.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-yellow-500 mb-2">
                      ⏳ Lista de espera ({selectedTraining.lista_espera.length})
                    </h4>
                    <div className="space-y-1">
                      {selectedTraining.lista_espera.map((userId, index) => (
                        <div key={userId} className="flex items-center justify-between gap-2 p-2 bg-yellow-900/20 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-500 font-bold text-sm w-6">{index + 1}.</span>
                            <span className="text-white text-sm">{users[userId]?.nombre || 'Usuario'}</span>
                          </div>
                          {user?.role === 'entrenador' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                              onClick={async () => {
                                if (window.confirm(`¿Eliminar a ${users[userId]?.nombre || 'este usuario'} de la lista de espera?`)) {
                                  try {
                                    await axios.delete(`${API}/trainings/${selectedTraining.id}/kick/${userId}`);
                                    fetchTrainings();
                                    setSelectedTraining(prev => ({
                                      ...prev,
                                      lista_espera: prev.lista_espera.filter(p => p !== userId)
                                    }));
                                  } catch (error) {
                                    alert('Error al eliminar usuario: ' + (error.response?.data?.detail || error.message));
                                  }
                                }
                              }}
                              data-testid={`kick-waitlist-${userId}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
      {/* Muro de Actividad (Debajo del calendario) */}
      <div className="container mx-auto px-4 pb-8">
        <ActivityFeed />
      </div>
    </div>
  );
}

export default Dashboard;